// SPDX-License-Identifier: AGPL-3.0-or-later

import GuildMembers from '@app/features/member/state/GuildMembers';
import {addTaskComment, deleteTaskComment} from '@app/features/tasks/commands/TaskCommands';
import styles from '@app/features/tasks/components/TaskComments.module.css';
import type {Task} from '@app/features/tasks/state/Tasks';
import {Avatar} from '@app/features/ui/components/Avatar';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {PaperPlaneRightIcon, TrashIcon} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

const COMMENTS = msg({message: 'Comments', comment: 'Heading of the comments in the to-do edit window.'});
const NO_COMMENTS = msg({message: 'No comments yet.', comment: 'Empty comments list on a to-do.'});
const WRITE_COMMENT = msg({
	message: 'Write a comment… type @ to tag someone',
	comment: 'Placeholder of the comment box on a to-do.',
});
const SEND = msg({message: 'Send comment', comment: 'Button that posts a comment on a to-do.'});
const DELETE = msg({message: 'Delete comment', comment: 'Button that deletes your own comment.'});
const TAG_SOMEONE = msg({message: 'Tag someone', comment: 'Accessible label of the @ people list.'});

interface Person {
	id: string;
	name: string;
	username: string;
}

function when(iso: string, locale: string): string {
	const date = new Date(iso);
	const sameDay = date.toDateString() === new Date().toDateString();
	return sameDay
		? date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'})
		: date.toLocaleDateString(locale, {month: 'short', day: 'numeric'});
}

/** Lower case without accents, so "rudo" finds "Rūdolfs". */
function fold(value: string): string {
	return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The comment text with "@Name" of tagged people highlighted. */
function CommentText({content, names}: {content: string; names: Array<string>}) {
	if (names.length === 0) return <>{content}</>;
	const pattern = new RegExp(
		`(${names
			.sort((a, b) => b.length - a.length)
			.map((n) => `@${escapeRegExp(n)}`)
			.join('|')})`,
		'g',
	);
	return (
		<>
			{content.split(pattern).map((part, index) =>
				index % 2 === 1 ? (
					<span key={index} className={styles.mention} data-flx="tasks.task-comments.mention">
						{part}
					</span>
				) : (
					part
				),
			)}
		</>
	);
}

/**
 * Comments on a to-do: the list (oldest first) and a box to add one. Enter sends, Shift+Enter is a
 * new line, and typing @ opens a list of people to tag (they get a notification and see it in Activity).
 */
export const TaskComments = observer(function TaskComments({task}: {task: Task}) {
	const {i18n} = useLingui();
	const me = Users.currentUserId;
	const meUser = me ? Users.getUser(me) : undefined;
	const [text, setText] = useState('');
	const [sending, setSending] = useState(false);
	const [tagged, setTagged] = useState<Array<Person>>([]);
	const [tagQuery, setTagQuery] = useState<string | null>(null);
	const [tagIndex, setTagIndex] = useState(0);
	const listRef = useRef<HTMLOListElement | null>(null);
	const inputRef = useRef<HTMLTextAreaElement | null>(null);
	const comments = task.comments ?? [];

	useEffect(() => {
		listRef.current?.scrollTo({top: listRef.current.scrollHeight});
	}, [comments.length]);

	const nameOf = (id: string) => {
		const member = GuildMembers.getMembers(task.guild_id).find((m) => m.user.id === id);
		const user = Users.getUser(id);
		return member?.nick || user?.globalName || user?.username || '';
	};

	const people: Array<Person> = GuildMembers.getMembers(task.guild_id)
		.filter((member) => member.user.id !== me)
		.map((member) => ({
			id: member.user.id,
			name: member.nick || member.user.globalName || member.user.username,
			username: member.user.username,
		}));
	const matches =
		tagQuery == null
			? []
			: people
					.filter((person) => fold(`${person.name} ${person.username}`).includes(fold(tagQuery)))
					.sort((a, b) => a.name.localeCompare(b.name))
					.slice(0, 6);

	/** Finds an "@word" being typed right before the cursor. */
	const updateTagQuery = (value: string, caret: number) => {
		const before = value.slice(0, caret);
		const match = /(?:^|\s)@([^\s@]{0,30})$/.exec(before);
		setTagQuery(match ? match[1] : null);
		setTagIndex(0);
	};

	const pick = (person: Person) => {
		const el = inputRef.current;
		const caret = el?.selectionStart ?? text.length;
		const before = text.slice(0, caret).replace(/@([^\s@]{0,30})$/, `@${person.name} `);
		const next = before + text.slice(caret);
		setText(next);
		setTagged((current) => (current.some((p) => p.id === person.id) ? current : [...current, person]));
		setTagQuery(null);
		requestAnimationFrame(() => {
			el?.focus();
			el?.setSelectionRange(before.length, before.length);
		});
	};

	const send = async () => {
		const content = text.trim();
		if (!content || sending) return;
		setSending(true);
		const mentionIds = tagged.filter((person) => content.includes(`@${person.name}`)).map((person) => person.id);
		const ok = await addTaskComment(task, content, mentionIds);
		setSending(false);
		if (ok) {
			setText('');
			setTagged([]);
		}
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (matches.length > 0) {
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				const step = event.key === 'ArrowDown' ? 1 : -1;
				setTagIndex((index) => (index + step + matches.length) % matches.length);
				return;
			}
			if (event.key === 'Enter' || event.key === 'Tab') {
				event.preventDefault();
				pick(matches[tagIndex] ?? matches[0]);
				return;
			}
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				setTagQuery(null);
				return;
			}
		}
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			if (tagQuery) return;
			void send();
		}
	};

	return (
		<section className={styles.root} aria-label={i18n._(COMMENTS)} data-flx="tasks.task-comments.root">
			<h3 className={styles.heading} data-flx="tasks.task-comments.heading">
				{i18n._(COMMENTS)}
				{comments.length > 0 && (
					<span className={styles.count} data-flx="tasks.task-comments.count">
						{comments.length}
					</span>
				)}
			</h3>
			{comments.length === 0 ? (
				<p className={styles.empty} data-flx="tasks.task-comments.empty">
					{i18n._(NO_COMMENTS)}
				</p>
			) : (
				<ol className={styles.list} ref={listRef} data-flx="tasks.task-comments.list">
					{comments.map((comment) => {
						const author = Users.getUser(comment.author_id);
						const names = (comment.mention_ids ?? []).map(nameOf).filter(Boolean);
						return (
							<li key={comment.id} className={styles.comment} data-flx="tasks.task-comments.comment">
								{author ? (
									<Avatar user={author} size={28} data-flx="tasks.task-comments.avatar" />
								) : (
									<span className={styles.avatarBlank} data-flx="tasks.task-comments.avatar-blank" />
								)}
								<div className={styles.bubble} data-flx="tasks.task-comments.bubble">
									<div className={styles.meta} data-flx="tasks.task-comments.meta">
										<span className={styles.author} data-flx="tasks.task-comments.author">
											{nameOf(comment.author_id) || '…'}
										</span>
										<span className={styles.time} data-flx="tasks.task-comments.time">
											{when(comment.created_at, i18n.locale)}
										</span>
										{comment.author_id === me && (
											<button
												type="button"
												className={styles.delete}
												aria-label={i18n._(DELETE)}
												title={i18n._(DELETE)}
												onClick={() => void deleteTaskComment(task, comment.id)}
												data-flx="tasks.task-comments.delete.button"
											>
												<TrashIcon size={14} aria-hidden="true" data-flx="tasks.task-comments.trash-icon" />
											</button>
										)}
									</div>
									<p className={styles.text} data-flx="tasks.task-comments.text">
										<CommentText content={comment.content} names={names} data-flx="tasks.task-comments.comment-text" />
									</p>
								</div>
							</li>
						);
					})}
				</ol>
			)}
			<div className={styles.composerWrap} data-flx="tasks.task-comments.composer-wrap">
				{matches.length > 0 && (
					<ul className={styles.tagList} role="listbox" aria-label={i18n._(TAG_SOMEONE)} data-flx="tasks.task-comments.tag-list">
						{matches.map((person, index) => {
							const user = Users.getUser(person.id);
							return (
								<li key={person.id} role="option" aria-selected={index === tagIndex} data-flx="tasks.task-comments.tag-option">
									<button
										type="button"
										className={clsx(styles.tagItem, index === tagIndex && styles.tagItemOn)}
										onMouseDown={(event) => {
											event.preventDefault();
											pick(person);
										}}
										onMouseEnter={() => setTagIndex(index)}
										data-flx="tasks.task-comments.tag-item.pick.button"
									>
										{user ? (
											<Avatar user={user} size={22} data-flx="tasks.task-comments.tag-avatar" />
										) : (
											<span className={styles.avatarBlank} data-flx="tasks.task-comments.tag-avatar-blank" />
										)}
										{person.name}
									</button>
								</li>
							);
						})}
					</ul>
				)}
				<div className={styles.composer} data-flx="tasks.task-comments.composer">
					{meUser && (
						<span className={styles.me} data-flx="tasks.task-comments.me">
							<Avatar user={meUser} size={24} data-flx="tasks.task-comments.me-avatar" />
						</span>
					)}
					<textarea
						ref={inputRef}
						className={styles.input}
						rows={1}
						maxLength={2000}
						value={text}
						placeholder={i18n._(WRITE_COMMENT)}
						aria-label={i18n._(WRITE_COMMENT)}
						onChange={(event) => {
							setText(event.target.value);
							updateTagQuery(event.target.value, event.target.selectionStart ?? event.target.value.length);
						}}
						onKeyDown={onKeyDown}
						onBlur={() => setTagQuery(null)}
						data-flx="tasks.task-comments.input"
					/>
					<button
						type="button"
						className={styles.send}
						aria-label={i18n._(SEND)}
						title={i18n._(SEND)}
						disabled={!text.trim() || sending}
						onClick={() => void send()}
						data-flx="tasks.task-comments.send.button"
					>
						<PaperPlaneRightIcon size={18} weight="fill" aria-hidden="true" data-flx="tasks.task-comments.send-icon" />
					</button>
				</div>
			</div>
		</section>
	);
});
